import type { FetchTrackedOneGraphOperation } from './ApolloNativeModule';
import type { FragmentRegistryAPI } from '@apollo/client/cache';

import { createHttpHeaderLink, typePolicies as defaultTypePolicies } from './apolloClientShared';

import {
    ApolloClient,
    type ApolloCache,
    type ApolloLink,
    type DefaultOptions,
    from,
    InMemoryCache,
    type NormalizedCacheObject,
    type TypePolicies,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';

const ERROR_POLICY_DEFAULTS: DefaultOptions = {
    watchQuery: { errorPolicy: 'all' },
    query: { errorPolicy: 'all' },
    mutate: { errorPolicy: 'all' },
};

function mergeDefaultOptions(overrides?: DefaultOptions): DefaultOptions {
    if (!overrides) return ERROR_POLICY_DEFAULTS;
    return {
        watchQuery: { ...ERROR_POLICY_DEFAULTS.watchQuery, ...overrides.watchQuery },
        query: { ...ERROR_POLICY_DEFAULTS.query, ...overrides.query },
        mutate: { ...ERROR_POLICY_DEFAULTS.mutate, ...overrides.mutate },
    };
}

interface CreateOneGraphApolloClientOptions {
    fetch?: FetchTrackedOneGraphOperation;
    trackingKey?: string;
    defaultOptions?: DefaultOptions;
    cache?: ApolloCache<NormalizedCacheObject>;
    platformOptions?: {
        web?: {
            graphqlUrl?: string;
            graphqlApiKey?: string;
        };
    };
    typePolicies?: TypePolicies;
    errorLink?: ApolloLink;
    prforceGroups?: string;
    fragments?: FragmentRegistryAPI;
    placementId?: string;
}

export function createOneGraphApolloClient({
    trackingKey,
    defaultOptions,
    cache,
    platformOptions,
    typePolicies,
    errorLink,
    prforceGroups,
    fragments,
    placementId,
}: CreateOneGraphApolloClientOptions): ApolloClient<NormalizedCacheObject> {
    const defaultAuthLink = setContext(({}, { headers }) => {
        return {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            headers: {
                ...headers,
                'indeed-api-key': platformOptions?.web?.graphqlApiKey,
            },
        };
    });

    const defaultWebHttpLink = new BatchHttpLink({
        credentials: 'include',
        uri: platformOptions?.web?.graphqlUrl,
        batchMax: 5,
        batchInterval: 5,
    });

    return new ApolloClient({
        cache:
            cache ??
            new InMemoryCache({
                typePolicies: Object.assign({}, defaultTypePolicies, typePolicies),
                fragments,
            }),
        defaultOptions: mergeDefaultOptions(defaultOptions),
        link: from([
            createHttpHeaderLink([
                ...(trackingKey ? [{ name: 'indeed-root-tracking-key', value: trackingKey }] : []),
                ...(prforceGroups ? [{ name: 'indeed-prforce-groups', value: prforceGroups }] : []),
                ...(placementId ? [{ name: 'indeed-placementid', value: placementId }] : []),
            ]),
            ...(errorLink ? [errorLink] : []),
            defaultAuthLink.concat(defaultWebHttpLink),
        ]),
    });
}
